/**
 * @file BackdropFrameGroup.tsx
 * @description Группа «Раскладка сайта» вкладки «Фон и рамка» (phase19.16
 * Р1; SB82; SB122 — группа-карточка; партия SB120, этап 2 —
 * uiux-sb120-stage2 §2, §10 вопрос 3; данные db-design-sb120-stage2 §1
 * `frame.maxWidth` int 960–1920, `frame.shadow` 0–100 %):
 *
 * ```
 * ┌──────────┐┌──────────┐     FrameLayoutField — OptionCards со схемами
 * │██████████││░┌──────┐░│     (SB122: схемы вместо слов, вместо Segmented)
 * │ Во всю   ││░│По    │░│
 * └──────────┘└──────────┘
 * Ширина рамки ├──────●─┤ [ 1600 ] px  [1280][1440][1536][1600]
 * Тень рамки   ├───●────┤ [  50 ] %    [Нет 0][Как было 50][100]
 * ```
 *
 * При «Во всю ширину» ширина и тень не прячутся, а серые с пояснением
 * «Только при «По центру»» (uiux-settings-groups §6).
 */

import { SITE_THEME_LIMITS, type SiteTheme } from "@chaiberi/shared";

import { FieldHint, SettingsGroup } from "../../ui/settings-groups";
import { NumberSliderField } from "../../site-fields/NumberSliderField";
import { OptionCards } from "../OptionCards";
import { FRAME_SHADOW_PRESETS, FRAME_WIDTH_PRESETS, FRAME_WIDTH_RECOMMENDED, outOfRecommended } from "../../../lib/site-view/appearance-numbers";
import { frameSummary } from "../../../lib/site-view/backdrop-summary";

type Frame = SiteTheme["frame"];

const { frameMaxWidth: FW, frameShadow: FS } = SITE_THEME_LIMITS;

/** Схема раскладки: сайт (тёмное) на фоне страницы (светлое). */
function FrameScheme({ mode }: { mode: Frame["mode"] }) {
  return (
    <span className="flex h-10 w-16 items-stretch justify-center rounded-sm bg-outline-variant/40">
      <span className={mode === "full" ? "w-full rounded-sm bg-on-surface-variant/70" : "mx-auto w-3/5 rounded-sm bg-on-surface-variant/70"} />
    </span>
  );
}

const MODES: ReadonlyArray<{ value: Frame["mode"]; label: string; hint: string }> = [
  { value: "full", label: "Во всю ширину", hint: "Сайт до краёв окна" },
  { value: "boxed", label: "По центру", hint: "Рамкой на фоне страницы" },
];

interface Props {
  frame: Frame;
  onFrame: (patch: Partial<Frame>) => void;
  onPreview?: (patch: Partial<Frame>) => void;
}

/** Переключатель «Во всю ширину / По центру» схемами. */
export function FrameLayoutField({ value, onChange }: { value: Frame["mode"]; onChange: (mode: Frame["mode"]) => void }) {
  return (
    <OptionCards
      aria-label="Раскладка сайта"
      columns={2}
      value={value}
      onChange={onChange}
      options={MODES.map((m) => ({ ...m, preview: <FrameScheme mode={m.value} /> }))}
    />
  );
}

export function BackdropFrameGroup({ frame, onFrame, onPreview }: Props) {
  const off = frame.mode === "full";
  return (
    <SettingsGroup
      id="frame"
      title="Раскладка сайта"
      tocLabel="Раскладка"
      hint="Как сайт стоит на широком экране."
      summary={frameSummary(frame)}
      status={!off && outOfRecommended(frame.maxWidth, FRAME_WIDTH_RECOMMENDED) ? "warning" : undefined}
    >
      <FrameLayoutField value={frame.mode} onChange={(mode) => onFrame({ mode })} />
      <NumberSliderField
        label="Ширина рамки"
        value={frame.maxWidth}
        inherit={{ value: FW.default, label: `${FW.default} px` }}
        min={FW.min}
        max={FW.max}
        step={1}
        integer
        largeStep={40}
        unit="px"
        presets={FRAME_WIDTH_PRESETS}
        recommended={FRAME_WIDTH_RECOMMENDED}
        disabled={off}
        onChange={(v) => onFrame({ maxWidth: v ?? FW.default })}
        onPreview={onPreview ? (v) => onPreview({ maxWidth: v ?? FW.default }) : undefined}
      />
      <NumberSliderField
        label="Тень рамки"
        value={frame.shadow}
        inherit={{ value: 0, label: "без тени" }}
        min={FS.min}
        max={FS.max}
        step={1}
        unit="%"
        presets={FRAME_SHADOW_PRESETS}
        disabled={off}
        onChange={(v) => onFrame({ shadow: v ?? 0 })}
        onPreview={onPreview ? (v) => onPreview({ shadow: v ?? 0 }) : undefined}
      />
      {off ? <FieldHint className="mt-0">Ширина и тень рамки — только при «По центру».</FieldHint> : null}
      <FieldHint className="mt-0">На телефоне и планшете уже рамки сайт всегда во всю ширину.</FieldHint>
      <FieldHint className="mt-0">Блоки с «Фоном за рамку сайта» выходят фоном до краёв окна — настройка у блока.</FieldHint>
    </SettingsGroup>
  );
}
